// ─────────────────────────────────────────────────────────────────────────────
// themeSlice.ts — Redux slice for theme / dark-mode state
//
// Persisted via redux-persist so the user's preference survives page reloads.
// The actual 'dark' class on <html> is applied by the ThemeProvider component
// which subscribes to this slice.
// ─────────────────────────────────────────────────────────────────────────────

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Theme } from '@/types/cv.types';

interface ThemeState {
  /** User-chosen theme mode */
  mode: Theme;
  /** Resolved effective theme after applying 'system' preference */
  resolved: 'light' | 'dark';
}

// Detect the OS colour scheme at startup
const systemPrefersDark = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

// Default theme for first-time visitors (no persisted preference yet) is
// 'dark', regardless of OS preference — overridden once redux-persist
// rehydrates a real stored choice (including a stored 'system' choice).
const initialState: ThemeState = {
  mode: 'dark',
  resolved: 'dark',
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,

  reducers: {
    /**
     * setTheme — Updates both the user preference and the resolved value.
     * When 'system' is chosen, the resolved value reflects the OS setting.
     */
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.mode = action.payload;
      if (action.payload === 'system') {
        state.resolved = systemPrefersDark() ? 'dark' : 'light';
      } else {
        state.resolved = action.payload;
      }
    },

    /**
     * syncSystemTheme — Called when the OS colour-scheme media query fires.
     * Only has an effect when the user has chosen 'system' mode.
     */
    syncSystemTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      if (state.mode === 'system') {
        state.resolved = action.payload;
      }
    },
  },
});

export const { setTheme, syncSystemTheme } = themeSlice.actions;
export default themeSlice.reducer;
